import { mountToAppShell } from './appShell'
import { FONTS, NeonColors, neonButton, neonPanel, neonTitleGlow } from './neon-theme'

export interface ErrorDialogOptions {
  title?: string
  message: string
  onClose?: () => void
  onSendLogs?: () => Promise<void>
}

let activeDialog: HTMLDivElement | null = null

function removeDialog(): void {
  activeDialog?.remove()
  activeDialog = null
}

export function hideErrorDialog(): void {
  removeDialog()
}

export function showErrorDialog(options: ErrorDialogOptions): void {
  removeDialog()

  const overlay = document.createElement('div')
  overlay.style.cssText = `
    position: absolute;
    inset: 0;
    z-index: 9998;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background:
      radial-gradient(circle at top, rgba(251,191,36,0.12), transparent 30%),
      radial-gradient(circle at bottom, rgba(0,221,255,0.08), transparent 32%),
      rgba(0,2,10,0.84);
    backdrop-filter: blur(10px);
  `

  const card = document.createElement('div')
  card.style.cssText = `
    ${neonPanel({ blur: true })}
    position: relative;
    width: min(420px, 100%);
    padding: 28px 24px 22px;
    border-radius: 24px;
    border: 1px solid rgba(255,255,255,0.1);
    box-shadow:
      0 24px 80px rgba(0,0,0,0.55),
      0 0 36px rgba(251,191,36,0.16),
      inset 0 0 0 1px rgba(255,255,255,0.04);
    overflow: hidden;
  `

  const glow = document.createElement('div')
  glow.style.cssText = `
    position: absolute;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,0.32) 0%, rgba(251,191,36,0.08) 40%, transparent 72%);
    pointer-events: none;
  `
  card.appendChild(glow)

  const image = document.createElement('img')
  image.src = '/assets/ui/oops-snake.webp'
  image.alt = 'Oops snake'
  image.style.cssText = `
    position: relative;
    display: block;
    width: min(210px, 62vw);
    max-width: 100%;
    margin: 0 auto 10px;
    filter: drop-shadow(0 0 24px rgba(0,255,136,0.28));
    user-select: none;
    pointer-events: none;
  `
  card.appendChild(image)

  const title = document.createElement('div')
  title.textContent = options.title ?? 'Oops'
  title.style.cssText = `
    ${neonTitleGlow()}
    position: relative;
    text-align: center;
    font-size: clamp(2.2rem, 8vw, 3rem);
    line-height: 0.95;
    margin: 6px 0 12px;
  `
  card.appendChild(title)

  const body = document.createElement('div')
  body.textContent = options.message
  body.style.cssText = `
    position: relative;
    color: ${NeonColors.textBright};
    font-family: ${FONTS.body};
    font-size: 1rem;
    line-height: 1.45;
    text-align: center;
    margin: 0 auto 18px;
    max-width: 320px;
  `
  card.appendChild(body)

  const status = document.createElement('div')
  status.style.cssText = `
    min-height: 20px;
    margin-bottom: 14px;
    text-align: center;
    font-family: ${FONTS.body};
    font-size: 0.88rem;
    color: ${NeonColors.textMuted};
  `
  card.appendChild(status)

  const buttons = document.createElement('div')
  buttons.style.cssText = `
    position: relative;
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  `

  const closeButton = document.createElement('button')
  closeButton.type = 'button'
  closeButton.textContent = 'Close'
  closeButton.style.cssText = `
    ${neonButton('rgba(15,23,42,0.92)', 'rgba(148,163,184,0.16)')}
    min-width: 132px;
    color: ${NeonColors.textBright};
    border: 1px solid rgba(148,163,184,0.35);
    box-shadow: 0 0 18px rgba(148,163,184,0.08);
  `
  closeButton.addEventListener('click', () => {
    removeDialog()
    options.onClose?.()
  })
  buttons.appendChild(closeButton)

  const sendButton = document.createElement('button')
  sendButton.type = 'button'
  sendButton.textContent = 'Send logs'
  sendButton.style.cssText = `
    ${neonButton(NeonColors.primary, NeonColors.primaryGlow)}
    min-width: 176px;
  `
  sendButton.addEventListener('click', async () => {
    if (!options.onSendLogs) {
      status.textContent = 'Logs are unavailable right now.'
      status.style.color = NeonColors.danger
      return
    }

    sendButton.disabled = true
    closeButton.disabled = true
    sendButton.style.opacity = '0.85'
    closeButton.style.opacity = '0.85'
    status.textContent = 'Sending logs...'
    status.style.color = NeonColors.info

    try {
      await options.onSendLogs()
      status.textContent = 'Logs were sent to Telegram.'
      status.style.color = NeonColors.success
      sendButton.textContent = 'Sent'
    } catch {
      status.textContent = 'Could not send logs. Please try again.'
      status.style.color = NeonColors.danger
      sendButton.disabled = false
      closeButton.disabled = false
      sendButton.style.opacity = '1'
      closeButton.style.opacity = '1'
    }
  })
  buttons.appendChild(sendButton)

  card.appendChild(buttons)
  overlay.appendChild(card)
  mountToAppShell(overlay)
  activeDialog = overlay
}
